import { useState, useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { UseNCFileContentFetchingParams, UseNCFileContentFetchingResult } from './types';

export const useNCFileContentFetching = ({
  oldVersion,
  newVersion,
  getNCFile,
  retrySignal = 0,
}: UseNCFileContentFetchingParams): UseNCFileContentFetchingResult => {
  const [oldContent, setOldContent] = useState<string | null>(null);
  const [newContent, setNewContent] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = useCallback(
    async (shouldUpdate: () => boolean) => {
      if (!oldVersion || !newVersion) {
        if (shouldUpdate()) {
          setError('Missing version information');
          setLoading(false);
        }
        return;
      }

      try {
        if (shouldUpdate()) {
          setLoading(true);
          setError(null);
        }

        // Check transfer states
        const oldState = oldVersion.ncFileTransferFromMachineState;
        const newState = newVersion.ncFileTransferFromMachineState;

        // Validate both versions are ready
        if (oldState !== 'Success') {
          throw new Error(`Old version (${oldVersion.sequenceNumber}) transfer state: ${oldState}`);
        }
        if (newState !== 'Success') {
          throw new Error(`New version (${newVersion.sequenceNumber}) transfer state: ${newState}`);
        }

        // Note: We skip isNcFileContentAvailable check - manually uploaded NC files have this flag as false (accroding to swagger api)
        // but content is still fetchable via fileContentHash (same approach as NCFile/index.js component)
        // if (!oldVersion.isNcFileContentAvailable) {
        //   throw new Error(`Content not available for version ${oldVersion.sequenceNumber}`);
        // }
        // if (!newVersion.isNcFileContentAvailable) {
        //   throw new Error(`Content not available for version ${newVersion.sequenceNumber}`);
        // }

        // Get content hashes
        const oldHash = oldVersion.fileContentHash;
        const newHash = newVersion.fileContentHash;

        if (!oldHash) {
          throw new Error(`Content hash missing for version ${oldVersion.sequenceNumber}`);
        }
        if (!newHash) {
          throw new Error(`Content hash missing for version ${newVersion.sequenceNumber}`);
        }

        // Fetch both files in parallel
        const [oldResult, newResult] = await Promise.all([getNCFile(oldHash, true), getNCFile(newHash, true)]);

        if (!shouldUpdate()) {
          return;
        }

        setOldContent(oldResult.data);
        setNewContent(newResult.data);
        setLoading(false);
      } catch (err: any) {
        if (!shouldUpdate()) {
          return;
        }
        console.error('Error fetching NC file content:', err);
        setError(err.message || 'Failed to fetch NC file content');
        setLoading(false);
      }
    },
    [oldVersion, newVersion, getNCFile, retrySignal]
  );

  useFocusEffect(
    useCallback(() => {
      let isActive = true;
      const shouldUpdate = () => isActive;
      fetchContent(shouldUpdate);
      return () => {
        isActive = false;
      };
    }, [fetchContent])
  );

  return {
    oldContent,
    newContent,
    loading,
    error,
    oldTransferState: oldVersion?.ncFileTransferFromMachineState || null,
    newTransferState: newVersion?.ncFileTransferFromMachineState || null,
  };
};
